import { useEffect, useMemo, useState } from 'react';
import { useAppFeatures } from '../app-features/api.js';
import { useQuery } from '@tanstack/react-query';
import {
  listMedia,
  listVocab,
  mediaListQueryKey,
  MEDIA_VOCAB_QUERY_KEY,
  type AiInvolvement,
  type ComplianceStatus,
  type HotelMedia,
  type MediaFilter,
  type Orientation,
  type Vocab,
  type VocabCategory,
} from './api.js';
import { MediaBadges } from './media-badges.js';
import { AI_INVOLVEMENT_LABEL } from './media-compliance-fields.js';
import { BLOCKER_REASON } from './media-compliance-copy.js';
import { Button } from '../../components/ui/button.js';
import { PencilIcon } from '../../components/ui/icons.js';
import { cn } from '../../lib/utils.js';

const ORIENTATION_LABEL: Record<Orientation, string> = {
  portrait: 'Álló',
  square: 'Négyzetes',
  landscape: 'Fekvő',
  panorama: 'Panoráma',
};

const ORIENTATIONS: Orientation[] = ['portrait', 'square', 'landscape', 'panorama'];

const COMPLIANCE_STATUS_LABEL: Record<ComplianceStatus, string> = {
  attachable: 'Csatolható',
  unknown: 'Bizonytalan',
  blocked: 'Blokkolt',
};

const COMPLIANCE_STATUSES: ComplianceStatus[] = ['attachable', 'unknown', 'blocked'];

const AI_INVOLVEMENTS: AiInvolvement[] = ['none', 'ai_edited', 'ai_generated', 'unknown'];

/** How long a caption search waits after the last keystroke, in ms. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The server's schema is `q: z.string().min(2)` (`packages/schemas/src/
 * hotel-media.ts`), so a one-character search is a 400 — and a 400 leaves the
 * PREVIOUS, unfiltered list on screen. A single character therefore never
 * becomes a request; the field says so instead.
 */
const SEARCH_MIN_LENGTH = 2;

interface TagGroup {
  category: VocabCategory;
  tags: { id: string; tag: string; archived: boolean }[];
}

/**
 * Tag chips grouped by category, in the categories' own `position` order —
 * the filter bar's mental model (spec: several tags together NARROW the
 * result, so grouping must not imply an OR between categories that isn't
 * there).
 *
 * Archived categories/tags are NOT dropped: an old image can still carry an
 * archived tag, and hiding it from the filter surface would make that image
 * unfindable by anything but scrolling. They render marked "(archív)" —
 * visible and still clickable — instead of being offered as if current.
 */
function groupTagsByCategory(vocab: Vocab): TagGroup[] {
  const byId = new Map(vocab.categories.map((c) => [c.id, c] as const));
  const groups = new Map<string, TagGroup>();
  for (const category of [...vocab.categories].sort((a, b) => a.position - b.position)) {
    groups.set(category.id, { category, tags: [] });
  }
  for (const tag of vocab.tags) {
    const category = byId.get(tag.categoryId);
    if (!category) continue;
    groups.get(category.id)?.tags.push({
      id: tag.id,
      tag: tag.tag,
      archived: tag.archivedAt !== null,
    });
  }
  return [...groups.values()].filter((g) => g.tags.length > 0);
}

/**
 * Returns `filter` with `tags` replaced, omitting the key entirely when the
 * list is empty — `exactOptionalPropertyTypes` forbids writing `tags:
 * undefined` explicitly, so an empty selection must drop the key instead.
 */
function withTags(filter: MediaFilter, tags: string[]): MediaFilter {
  const next: MediaFilter = { ...filter };
  delete next.tags;
  return tags.length > 0 ? { ...next, tags } : next;
}

/** Returns `filter` with `orientation` replaced, omitting the key when cleared. */
function withOrientation(filter: MediaFilter, orientation: Orientation | null): MediaFilter {
  const next: MediaFilter = { ...filter };
  delete next.orientation;
  return orientation ? { ...next, orientation } : next;
}

/**
 * Same "drop the key rather than write `undefined`" rule as `withTags` and
 * `withOrientation` (`exactOptionalPropertyTypes`), for the three filters
 * the bar gained in task B3.
 */
function withKey<K extends 'complianceStatus' | 'aiInvolvement' | 'q'>(
  filter: MediaFilter,
  key: K,
  value: MediaFilter[K] | null,
): MediaFilter {
  const next: MediaFilter = { ...filter };
  delete next[key];
  return value ? { ...next, [key]: value } : next;
}

export function MediaLibraryBrowser({
  hotelId,
  mode,
  selectedIds = [],
  onSelectionChange,
  maxSelection,
  onOpenDetail,
  canOverride = false,
  reloadToken,
}: {
  hotelId: string;
  /**
   * Three genuinely different situations, not two (fix round 1, task 31):
   * `gallery` — browsing; a card opens its detail dialog. `picker` — the
   * post editor's attach flow; a blocked card is visible but refuses
   * selection, with its reasons shown (a blocked image must never become
   * attachable just by being picked). `select` — the gallery's own
   * bulk-edit mode; EVERY card is selectable, precisely because the blocked
   * ones are the work a colleague is there to do (record where a whole shoot
   * came from, in bulk). `select` reused `picker`'s disable gate until this
   * fix, which made the September clean-up workflow impossible — see
   * task-31-report.md, Finding 2.
   */
  mode: 'gallery' | 'picker' | 'select';
  selectedIds?: string[];
  /**
   * The new selection, plus the loaded `HotelMedia` rows behind it. The
   * second argument is what lets the post editor tell a blocked pick from an
   * ordinary one WITHOUT re-fetching: a caller that doesn't care simply
   * declares one parameter. Rows the browser hasn't loaded (a pre-selection
   * from another page) are absent from it — documented, not hidden.
   */
  onSelectionChange?: (ids: string[], items: HotelMedia[]) => void;
  maxSelection?: number;
  onOpenDetail?: (media: HotelMedia) => void;
  /**
   * Whether THIS caller's user may override the fail-closed attach gate
   * (task A4 / C3). A prop, not an auth-store read: the same browser serves
   * the gallery page and the post editor, and only the caller knows whether
   * an override is meaningful there. With it, a blocked card in `picker`
   * mode becomes selectable and says so; without it the card stays refused,
   * exactly as before. It changes nothing in `gallery`/`select` mode.
   */
  canOverride?: boolean;
  /**
   * Bump this (any changing number) to force a reload from page one. A
   * caller that just ran a bulk op or a detail-dialog save increments it —
   * per the controller ruling, a refetch of a LATER page can never tell
   * whether a row that vanished from page one (deleted, or filtered out by
   * the change) should also vanish here, so the only correct move is to
   * drop everything accumulated and refetch from the start. Losing scroll
   * position is the honest cost of that: the list genuinely changed.
   */
  reloadToken?: number;
}) {
  const vocabQuery = useQuery<Vocab>({
    queryKey: MEDIA_VOCAB_QUERY_KEY,
    queryFn: () => listVocab(),
  });
  const tagGroups = useMemo(
    () => groupTagsByCategory(vocabQuery.data ?? { categories: [], tags: [] }),
    [vocabQuery.data],
  );

  const [filter, setFilter] = useState<MediaFilter>({});
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  // The loaded pages, accumulated as "További képek" is clicked. Filtering
  // always goes to the server (a fresh `filter` resets this — never sliced
  // or searched client-side out of what happens to already be loaded).
  const [items, setItems] = useState<HotelMedia[]>([]);
  // What the user has typed so far. Kept separate from `filter.q` so the
  // input stays responsive while only the settled value becomes a request —
  // a caption search on a 20-hotel library is not worth one round trip per
  // keystroke.
  const [searchDraft, setSearchDraft] = useState('');

  const mediaQuery = useQuery({
    queryKey: [...mediaListQueryKey(hotelId), filter, cursor],
    // The cursor the request was made with travels back with the page. The
    // accumulating effect below decides replace-vs-append from THIS, not from
    // the `cursor` state as it happens to stand when the response lands.
    queryFn: async () => ({ ...(await listMedia(hotelId, filter, cursor)), requestCursor: cursor }),
  });

  // `reloadToken` forces a reload from page one. Deliberately NOT skipped on
  // the first render: at mount `cursor` is already `undefined` and `items`
  // already `[]`, so re-setting them is a no-op there, and the effect only
  // ever needs to fire again when a caller bumps the token after a change.
  useEffect(() => {
    setCursor(undefined);
    setItems([]);
  }, [reloadToken]);

  // Debounced caption search. The timer is cleared on every keystroke and on
  // unmount, so only the last value survives. The guard keeps a settled
  // draft from resetting pagination on unrelated re-renders: without it,
  // every render with an unchanged draft would push a new `filter` object
  // and drop the accumulated pages.
  useEffect(() => {
    const trimmed = searchDraft.trim();
    // Egy karakter nem megy el kérésnek (lásd `SEARCH_MIN_LENGTH`); az üres
    // mező viszont igen, mert az a szűrő törlése.
    if (trimmed.length > 0 && trimmed.length < SEARCH_MIN_LENGTH) return;
    if (trimmed === (filter.q ?? '')) return;
    const timer = setTimeout(() => {
      setFilter((prev) => withKey(prev, 'q', trimmed || null));
      setCursor(undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // `filter.q` is the committed value this effect compares against;
    // re-running on a `filter` identity change (a tag chip, say) is
    // deliberate and idempotent thanks to the guard above.
  }, [searchDraft, filter.q]);

  useEffect(() => {
    if (!mediaQuery.data) return;
    const { data: fresh, requestCursor } = mediaQuery.data;
    // First page (no cursor) REPLACES — this is also what a `reloadToken`
    // bump lands on, since it resets `cursor` to `undefined` first. A later
    // page APPENDS, skipping rows already held: a refetch of page two says
    // nothing about whether a row on page one still belongs in the list
    // (deleted, or filtered out by whatever changed), so this effect never
    // tries to reconcile an already-loaded page against a background
    // refetch — callers that change the library go through `reloadToken`
    // instead, which drops the accumulated pages entirely. The id filter
    // keeps a plain refetch of that page from doubling its rows.
    setItems((prev) => {
      if (!requestCursor) return fresh;
      const held = new Set(prev.map((i) => i.id));
      return [...prev, ...fresh.filter((i) => !held.has(i.id))];
    });
    // `dataUpdatedAt` — NOT `data` alone — is what says "a page landed".
    //
    // React Query's default structural sharing returns the PREVIOUS data
    // object when a refetch is deep-equal to what it already had. So after a
    // detail-dialog save that changed nothing the list can see, `data` keeps
    // its identity, this effect does not run, and the `items` that the
    // `reloadToken` effect has just emptied stay empty: the grid goes blank
    // and only a full page reload brings it back. `dataUpdatedAt` moves on
    // every successful fetch, identical payload or not.
    //
    // Replace-vs-append reads `requestCursor` off the response rather than
    // the `cursor` state, so a page that lands late cannot be appended under
    // a cursor that has since changed.
  }, [mediaQuery.data, mediaQuery.dataUpdatedAt]);

  function toggleTag(tag: string) {
    const current = filter.tags ?? [];
    const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    setFilter(withTags(filter, next));
    setCursor(undefined);
  }

  function toggleOrientation(o: Orientation) {
    setFilter(withOrientation(filter, filter.orientation === o ? null : o));
    setCursor(undefined);
  }

  const features = useAppFeatures();
  const complianceOff = features.data?.mediaComplianceMode === 'off';

  function toggleComplianceStatus(s: ComplianceStatus) {
    setFilter(withKey(filter, 'complianceStatus', filter.complianceStatus === s ? null : s));
    setCursor(undefined);
  }

  function selectAiInvolvement(value: string) {
    setFilter(withKey(filter, 'aiInvolvement', (value || null) as AiInvolvement | null));
    setCursor(undefined);
  }

  /** The loaded rows behind a list of ids, in the ids' own order. */
  function resolve(ids: string[]): HotelMedia[] {
    const byId = new Map(items.map((i) => [i.id, i] as const));
    return ids.map((id) => byId.get(id)).filter((x): x is HotelMedia => x !== undefined);
  }

  function toggleSelect(m: HotelMedia) {
    if ((mode !== 'picker' && mode !== 'select') || !onSelectionChange) return;
    // Only the attach picker gates on attachability — bulk-select mode
    // exists specifically so a blocked image CAN be selected and fixed, and
    // an overriding admin picks the blocked one deliberately (the waiver is
    // then asked for at attach time, not here).
    if (mode === 'picker' && !m.attachable && !canOverride) return;
    if (selectedIds.includes(m.id)) {
      const next = selectedIds.filter((id) => id !== m.id);
      onSelectionChange(next, resolve(next));
      return;
    }
    if (maxSelection !== undefined && selectedIds.length >= maxSelection) return;
    const next = [...selectedIds, m.id];
    onSelectionChange(next, resolve(next));
  }

  const nextCursor = mediaQuery.data?.nextCursor;
  const searchTrimmed = searchDraft.trim();
  const searchTooShort = searchTrimmed.length > 0 && searchTrimmed.length < SEARCH_MIN_LENGTH;

  return (
    <div className="space-y-4">
      <div className="space-y-3 rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)] p-3">
        {vocabQuery.isError && (
          // Audit I11: a failed dictionary fetch used to make the whole tag
          // filter row vanish without a word — the colleague saw a gallery
          // with no tag filter and no clue why.
          <p
            role="alert"
            className="flex flex-wrap items-center gap-2 text-meta text-[var(--danger)]"
          >
            A címkeszótár nem tölthető be.
            <Button size="sm" variant="outline" onClick={() => void vocabQuery.refetch()}>
              Újra
            </Button>
          </p>
        )}
        {tagGroups.map(({ category, tags }) => (
          <div key={category.id} className="space-y-1">
            <div className="text-meta font-medium text-[var(--fg-2)]">
              {category.label}
              {category.archivedAt !== null && (
                <span className="ml-1 font-normal text-[var(--fg-faint)]">(archív)</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => {
                const active = (filter.tags ?? []).includes(t.tag);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTag(t.tag)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-pill border px-2 py-0.5 text-meta',
                      active
                        ? 'border-[var(--brand-500)] bg-[var(--brand-100)] text-[var(--brand-800)]'
                        : 'border-[var(--app-divider-2)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]',
                      t.archived && !active && 'italic text-[var(--fg-faint)]',
                    )}
                  >
                    {t.tag}
                    {t.archived && <span className="ml-1">(archív)</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-meta font-medium text-[var(--fg-2)]">Tájolás</span>
          {ORIENTATIONS.map((o) => {
            const active = filter.orientation === o;
            return (
              <label
                key={o}
                className={cn(
                  'cursor-pointer rounded-pill border px-2 py-0.5 text-meta',
                  active
                    ? 'border-[var(--brand-500)] bg-[var(--brand-100)] text-[var(--brand-800)]'
                    : 'border-[var(--app-divider-2)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]',
                )}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={active}
                  onChange={() => toggleOrientation(o)}
                  aria-label={ORIENTATION_LABEL[o]}
                />
                {ORIENTATION_LABEL[o]}
              </label>
            );
          })}
        </div>

        {!complianceOff && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <span className="text-meta font-medium text-[var(--fg-2)]">Jogállás</span>
            {COMPLIANCE_STATUSES.map((s) => {
              const active = filter.complianceStatus === s;
              return (
                <label
                  key={s}
                  className={cn(
                    'cursor-pointer rounded-pill border px-2 py-0.5 text-meta',
                    active
                      ? 'border-[var(--brand-500)] bg-[var(--brand-100)] text-[var(--brand-800)]'
                      : 'border-[var(--app-divider-2)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]',
                  )}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={active}
                    onChange={() => toggleComplianceStatus(s)}
                    aria-label={COMPLIANCE_STATUS_LABEL[s]}
                  />
                  {COMPLIANCE_STATUS_LABEL[s]}
                </label>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {!complianceOff && (
            <label className="flex items-center gap-1.5 text-meta font-medium text-[var(--fg-2)]">
              AI-érintettség
              <select
                aria-label="AI-érintettség"
                value={filter.aiInvolvement ?? ''}
                onChange={(e) => selectAiInvolvement(e.target.value)}
                className="rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-2 py-0.5 text-meta font-normal text-[var(--fg-1)]"
              >
                <option value="">Mindegy</option>
                {AI_INVOLVEMENTS.map((a) => (
                  <option key={a} value={a}>
                    {AI_INVOLVEMENT_LABEL[a]}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="flex flex-1 items-center gap-1.5 text-meta font-medium text-[var(--fg-2)]">
            Keresés a feliratban
            <input
              type="search"
              aria-label="Keresés a feliratban"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Például: medence este"
              className="min-w-0 flex-1 rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-2 py-0.5 text-meta font-normal text-[var(--fg-1)]"
            />
          </label>
          {searchTooShort && (
            <span className="text-meta font-normal text-[var(--fg-2)]">
              A kereséshez legalább két karakter kell.
            </span>
          )}
        </div>
      </div>

      {mediaQuery.isError ? (
        // A lista-hiba eddig néma volt: a lista-összeállító effekt kilépett, és
        // a KORÁBBI, szűretlen találatok maradtak a képernyőn — a kolléga azt
        // hitte, azok a keresésére adott válasz.
        <p role="alert" className="py-8 text-center text-body text-[var(--danger)]">
          A képek betöltése nem sikerült. Próbáld újra, vagy módosítsd a szűrőt.
        </p>
      ) : items.length === 0 && !mediaQuery.isLoading ? (
        <p className="py-8 text-center text-body text-[var(--fg-2)]">
          Nincs a szűrésnek megfelelő kép.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {items.map((m) => (
            <MediaCard
              key={m.id}
              media={m}
              mode={mode}
              selected={selectedIds.includes(m.id)}
              canOverride={canOverride}
              onToggle={() => toggleSelect(m)}
              onOpenDetail={onOpenDetail}
            />
          ))}
        </div>
      )}

      {typeof nextCursor === 'string' && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => setCursor(nextCursor)}>
            További képek
          </Button>
        </div>
      )}
    </div>
  );
}

function MediaCard({
  media,
  mode,
  selected,
  canOverride,
  onToggle,
  onOpenDetail,
}: {
  media: HotelMedia;
  mode: 'gallery' | 'picker' | 'select';
  selected: boolean;
  canOverride: boolean;
  onToggle: () => void;
  onOpenDetail?: ((media: HotelMedia) => void) | undefined;
}) {
  // Thumbnails only, never the original, EXCEPT for rows uploaded before the
  // thumbnail backfill ran — those have no derivative yet and must still be
  // visible, just heavier.
  const src = media.thumbUrl ?? media.url;
  const selectable = mode === 'picker' || mode === 'select';
  // Only the attach picker ever disables a card — bulk-select mode never
  // does, since selecting a blocked image is the whole point of it — and
  // even there, not for someone who may override the gate.
  const disabled = mode === 'picker' && !media.attachable && !canOverride;
  /** Picked deliberately by someone who may waive the gate: say so up front,
   *  so the dialog that follows is not a surprise. */
  const needsOverride = mode === 'picker' && !media.attachable && canOverride;

  function activate() {
    if (selectable) {
      if (disabled) return;
      onToggle();
      return;
    }
    onOpenDetail?.(media);
  }

  return (
    <div
      data-testid={`media-card-${media.id}`}
      role="button"
      tabIndex={0}
      aria-disabled={mode === 'picker' ? disabled : undefined}
      aria-pressed={selectable ? selected : undefined}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
      className={cn(
        'group flex flex-col gap-1.5 rounded-lg border p-1.5 text-left',
        disabled
          ? 'cursor-not-allowed border-[var(--app-divider)] opacity-60'
          : 'cursor-pointer border-[var(--app-divider)] hover:border-[var(--brand-400)]',
        selected && 'border-[var(--brand-500)] ring-2 ring-[var(--brand-500)]/40',
      )}
    >
      <div className="relative aspect-square overflow-hidden rounded-md bg-[var(--app-divider)]">
        <img
          src={src}
          // A non-empty alt: an empty string makes the browser treat the
          // <img> as decorative (ARIA role "none"), which would make it
          // invisible to `getByRole('img')` — and to screen readers, this is
          // hotel photography a colleague picks from, not chrome.
          alt={media.caption ?? 'Hotel kép'}
          loading="lazy"
          className="h-full w-full object-cover"
        />
        {/*
          Gallery mode's whole-card click already opens the detail dialog
          (where tags/compliance are edited), but that affordance is
          invisible until hovered/discovered. An explicit, labelled control
          gives the same action a name a colleague — and a test — can find
          directly, and it is what replaced the old `prompt()`-based tag
          editor: no browser dialog, ever.
        */}
        {mode === 'gallery' && onOpenDetail && (
          <button
            type="button"
            aria-label="Címkék szerkesztése"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail(media);
            }}
            className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-black/75 focus-visible:opacity-100 group-hover:opacity-100"
          >
            <PencilIcon size={12} />
          </button>
        )}
      </div>
      <MediaBadges media={media} />
      {needsOverride && (
        <span className="rounded-pill bg-[var(--warn-100,#fef3c7)] px-2 py-0.5 text-meta font-medium text-[var(--warn-800,#92400e)]">
          Felülbírálást igényel
        </span>
      )}
      {/*
        Picker mode never HIDES a non-attachable image — hiding it would read
        as "the photo is missing" and send the colleague hunting for it
        elsewhere. Instead it stays visible, greyed out, with the exact
        blocker sentences spelled out so they know what to fix and where.
        Select mode shows the same reasons on every blocked card too — even
        though it's fully selectable there, not greyed out — because someone
        selecting a batch to fix still benefits from seeing what's wrong with
        each one before they set the answer for the whole batch.
      */}
      {(disabled || needsOverride || (mode === 'select' && !media.attachable)) &&
        media.blockers.length > 0 && (
          <ul className="space-y-0.5 text-meta text-[var(--fg-2)]">
            {media.blockers.map((b) => (
              <li key={b}>{BLOCKER_REASON[b] ?? b}</li>
            ))}
          </ul>
        )}
    </div>
  );
}
